import { CloseIcon } from "./icons";
import {
  type RefObject,
  type ReactNode,
  useEffect,
  useId,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "@/shared/lib/cn";
import { IconButton } from "./IconButton";

const focusableSelector = [
  'button:not(:disabled):not([tabindex="-1"])',
  '[href]:not([tabindex="-1"])',
  'input:not(:disabled):not([type="hidden"]):not([hidden]):not([tabindex="-1"])',
  'select:not(:disabled):not([tabindex="-1"])',
  'textarea:not(:disabled):not([tabindex="-1"])',
  '[tabindex]:not([tabindex="-1"])',
].join(",");

// Overlapping modals can close in any order (for example, a picker and upload
// dialog closing in one render). Restore the original state only after the
// last modal releases its lock, rather than restoring another modal's snapshot.
const inertLocks = new WeakMap<HTMLElement, { count: number; previous: boolean }>();
function lockInert(node: HTMLElement): () => void {
  const lock = inertLocks.get(node) ?? { count: 0, previous: node.inert };
  lock.count++;
  inertLocks.set(node, lock);
  node.inert = true;
  return () => {
    if (--lock.count === 0) {
      node.inert = lock.previous;
      inertLocks.delete(node);
    }
  };
}
const scrollLocks = new WeakMap<HTMLElement, { count: number; previous: string }>();
function lockBodyScroll(): () => void {
  const body = document.body;
  const lock = scrollLocks.get(body) ?? { count: 0, previous: body.style.overflow };
  lock.count++;
  scrollLocks.set(body, lock);
  body.style.overflow = "hidden";
  return () => {
    if (--lock.count === 0) {
      body.style.overflow = lock.previous;
      scrollLocks.delete(body);
    }
  };
}

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  headerActions?: ReactNode;
  footer?: ReactNode;
  closeLabel?: string;
  dismissible?: boolean;
  alert?: boolean;
  initialFocusRef?: RefObject<HTMLElement | null>;
  /** Disable container focus for pickers that always provide focusable controls. */
  containerFocusable?: boolean;
  className?: string;
  contentClassName?: string;
  /** Keep the accessible title while showing only the close control. */
  headerMode?: "default" | "close-only";
  /** Keep the page at full visual strength for lightweight pickers. */
  scrim?: "quiet" | "none";
}

/** Modal dialog with focus trap/restore, inert background, and a quiet scrim. */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  headerActions,
  footer,
  closeLabel = "Close dialog",
  dismissible = true,
  alert = false,
  initialFocusRef,
  containerFocusable = true,
  className,
  contentClassName,
  headerMode = "default",
  scrim = "quiet",
}: DialogProps) {
  const titleId = useId();
  const descriptionId = useId();
  const overlayRef = useRef<HTMLDivElement>(null);
  useOverlayFocus({
    open,
    onClose,
    dismissible: dismissible && !alert,
    overlayRef,
    initialFocusRef,
    inertBackground: true,
    lockScroll: true,
  });
  if (!open) return null;
  return createPortal(
    <div
      ref={overlayRef}
      data-afterform-overlay=""
      className={cn(
        "fixed inset-0 z-50 flex items-center justify-center p-4",
        scrim === "quiet" ? "bg-[var(--nav-scrim)]" : "bg-transparent",
      )}
      onPointerDown={(event) => {
        if (dismissible && !alert && event.target === event.currentTarget)
          onClose();
      }}
    >
      <section
        role={alert ? "alertdialog" : "dialog"}
        tabIndex={containerFocusable ? -1 : undefined}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={
          description ? descriptionId : undefined
        }
        className={cn(
          "relative flex max-h-[calc(100dvh-32px)] w-full max-w-[640px] flex-col overflow-hidden studio-overlay rounded-[24px]",
          className,
        )}
      >
        <header
          className={cn(
            headerMode === "default"
              ? "flex items-start justify-between gap-4 px-5 py-4"
              : "pointer-events-none absolute inset-x-0 top-0 z-20 flex justify-end p-3",
          )}
        >
          <div className={headerMode === "close-only" ? "sr-only" : "min-w-0"}>
            <h2
              id={titleId}
              className="m-0 text-[17px] font-semibold leading-[1.3] tracking-[-0.014em] text-ink"
            >
              {title}
            </h2>
            {description ? (
              <p
                id={descriptionId}
                className="m-0 mt-1 text-[12.5px] leading-[1.5] text-ink-muted"
              >
                {description}
              </p>
            ) : null}
          </div>
          {headerMode === "default" && headerActions ? (
            <div className="ms-auto flex shrink-0 items-center gap-2">{headerActions}</div>
          ) : null}
          {dismissible && !alert ? (
            <IconButton
              size="sm"
              aria-label={closeLabel}
              onClick={onClose}
              className={cn(
                "text-ink-muted",
                headerMode === "close-only" &&
                  "pointer-events-auto",
              )}
            >
              <CloseIcon className="size-4" />
            </IconButton>
          ) : null}
        </header>
        {children ? (
          <div
            className={cn(
              "min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-[18px]",
              contentClassName,
            )}
          >
            {children}
          </div>
        ) : null}
        {footer ? (
          <footer className="flex flex-wrap items-center justify-end gap-2 px-5 py-[14px]">
            {footer}
          </footer>
        ) : null}
      </section>
    </div>,
    document.body,
  );
}

export interface SheetProps extends Omit<DialogProps, "alert" | "containerFocusable" | "headerActions"> {
  meta?: ReactNode;
}

/** Full-height task surface. The page stays visible; focus remains in the sheet. */
export function Sheet({
  open,
  onClose,
  title,
  description,
  meta,
  children,
  footer,
  closeLabel = "Close sheet",
  dismissible = true,
  initialFocusRef,
  className,
}: SheetProps) {
  const titleId = useId();
  const descriptionId = useId();
  const overlayRef = useRef<HTMLDivElement>(null);
  useOverlayFocus({
    open,
    onClose,
    dismissible,
    overlayRef,
    initialFocusRef,
    inertBackground: true,
    lockScroll: true,
  });
  if (!open) return null;
  return createPortal(
    <div
      ref={overlayRef}
      data-afterform-overlay=""
      className="pointer-events-none fixed inset-0 z-50 flex justify-end"
    >
      <section
        role="dialog"
        tabIndex={-1}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        className={cn(
          "pointer-events-auto flex h-full w-full max-w-[560px] flex-col studio-overlay",
          className,
        )}
      >
        <header className="flex items-start justify-between gap-4 border-b border-line-soft px-[18px] py-4">
          <div className="min-w-0">
            <h2
              id={titleId}
              className="m-0 text-base font-semibold tracking-[-0.014em] text-ink"
            >
              {title}
            </h2>
            {description ? (
              <p id={descriptionId} className="m-0 mt-1 text-xs text-ink-muted">
                {description}
              </p>
            ) : null}
          </div>
          {dismissible ? (
            <IconButton
              size="sm"
              aria-label={closeLabel}
              onClick={onClose}
              className="text-ink-muted"
            >
              <CloseIcon className="size-4" />
            </IconButton>
          ) : null}
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-[18px] py-4">
          {children}
        </div>
        {footer ? (
          <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-line-soft px-[18px] py-[13px]">
            {meta}
            <span className="ms-auto flex flex-wrap gap-2">{footer}</span>
          </footer>
        ) : null}
      </section>
    </div>,
    document.body,
  );
}

export function useOverlayFocus({
  open,
  onClose,
  dismissible,
  overlayRef,
  initialFocusRef,
  inertBackground,
  lockScroll,
}: {
  open: boolean;
  onClose: () => void;
  dismissible: boolean;
  overlayRef: RefObject<HTMLDivElement | null>;
  initialFocusRef?: RefObject<HTMLElement | null>;
  inertBackground: boolean;
  lockScroll: boolean;
}) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const openRef = useRef(open);
  openRef.current = open;
  const openerRef = useRef<HTMLElement | null>(null);

  // React autoFocus runs before the opening effect. Remember focus while closed
  // so that a field mounted inside the overlay cannot become its own opener.
  useEffect(() => {
    const rememberOpener = () => {
      if (!openRef.current) openerRef.current = document.activeElement as HTMLElement | null;
    };
    rememberOpener();
    document.addEventListener("focusin", rememberOpener);
    return () => document.removeEventListener("focusin", rememberOpener);
  }, []);

  useEffect(() => {
    if (!open) return;
    const active = document.activeElement as HTMLElement | null;
    const opener = active && !overlayRef.current?.contains(active) ? active : openerRef.current;
    const releaseScroll = lockScroll ? lockBodyScroll() : undefined;
    const background = inertBackground
      ? Array.from(document.body.children).filter(
          (node): node is HTMLElement =>
            node instanceof HTMLElement && node !== overlayRef.current && !node.hasAttribute("data-afterform-notifications"),
        )
      : [];
    const releaseBackground = background.map(lockInert);
    const frame = requestAnimationFrame(() => {
      if (initialFocusRef?.current && !initialFocusRef.current.matches(":disabled, [hidden], [aria-disabled=\"true\"]")) {
        initialFocusRef.current.focus();
        if (document.activeElement === initialFocusRef.current) return;
      }
      if (overlayRef.current?.contains(document.activeElement)) return;
      const panel = overlayRef.current?.querySelector<HTMLElement>(
        '[role="dialog"], [role="alertdialog"]',
      );
      (
        overlayRef.current?.querySelector<HTMLElement>(focusableSelector) ??
        panel
      )?.focus();
    });
    function onKeyDown(event: KeyboardEvent): void {
      const overlays = document.querySelectorAll<HTMLElement>(
        "[data-afterform-overlay]",
      );
      if (overlays.item(overlays.length - 1) !== overlayRef.current) return;
      if (event.key === "Escape" && dismissible) {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab") return;
      const notifications = document.querySelector<HTMLElement>("[data-afterform-notifications]");
      const focusable = [
        ...Array.from(overlayRef.current?.querySelectorAll<HTMLElement>(focusableSelector) ?? []),
        ...Array.from(notifications?.querySelectorAll<HTMLElement>(focusableSelector) ?? []),
      ];
      const panel = overlayRef.current?.querySelector<HTMLElement>(
        '[role="dialog"], [role="alertdialog"]',
      );
      if (!focusable.length) {
        event.preventDefault();
        panel?.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable.at(-1);
      // Popover ancestry survives its document.body portal. Tab can leave a
      // child dropdown through its trigger's next sibling without escaping the
      // modal or jumping back to the modal's close button.
      const activeElement = document.activeElement;
      const popover = activeElement?.closest<HTMLElement>("[data-popover-branch]");
      const branch = popover?.dataset.popoverBranch?.split(" ") ?? [];
      const ownsBranch = focusable.some((element) =>
        branch.includes(element.getAttribute("aria-controls") ?? ""),
      );
      if (popover && ownsBranch) {
        const innerControls = Array.from(popover.querySelectorAll<HTMLElement>(focusableSelector));
        const index = innerControls.indexOf(activeElement as HTMLElement);
        const next = innerControls[index + (event.shiftKey ? -1 : 1)];
        if (index >= 0 && next) return;
        event.preventDefault();
        for (let depth = branch.length - 1; depth >= 0; depth--) {
          const parent = depth > 0 ? document.getElementById(branch[depth - 1]!) : overlayRef.current;
          const siblings = Array.from(parent?.querySelectorAll<HTMLElement>(focusableSelector) ?? []);
          const triggerIndex = siblings.findIndex((element) => element.getAttribute("aria-controls") === branch[depth]);
          const sibling = siblings[triggerIndex + (event.shiftKey ? -1 : 1)];
          if (triggerIndex >= 0 && sibling) {
            sibling.focus();
            return;
          }
        }
        (event.shiftKey ? last : first)?.focus();
        return;
      }
      if (!overlayRef.current?.contains(document.activeElement) && !notifications?.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first)?.focus();
      } else if (
        event.shiftKey &&
        (document.activeElement === first || document.activeElement === panel)
      ) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener("keydown", onKeyDown);
      releaseBackground.forEach(release => release());
      releaseScroll?.();
      opener?.focus();
    };
  }, [
    dismissible,
    inertBackground,
    initialFocusRef,
    lockScroll,
    open,
    overlayRef,
  ]);
}
